// src/components/ScrollBars.jsx
import React, { useCallback, useRef, useEffect, useState } from "react";

export default function ScrollBars({ viewBox, diagramBounds, onViewBoxChange, setActiveInteraction }) {
  const horizontalThumbRef = useRef(null);
  const verticalThumbRef = useRef(null);
  const horizontalTrackRef = useRef(null);
  const verticalTrackRef = useRef(null);
  const [trackDimensions, setTrackDimensions] = useState({ horizontal: { width: 0 }, vertical: { height: 0 } });
  
  const horizontalDragRef = useRef({ active: false, startX: 0, startViewX: 0 });
  const verticalDragRef = useRef({ active: false, startY: 0, startViewY: 0 });

  const { minX, minY, maxX, maxY } = diagramBounds;
  const totalWidth = Math.max(1, maxX - minX);
  const totalHeight = Math.max(1, maxY - minY);
  
  // Update track dimensions after render
  useEffect(() => {
    const updateTrackDimensions = () => {
      if (horizontalTrackRef.current) {
        const horizontalRect = horizontalTrackRef.current.getBoundingClientRect();
        setTrackDimensions(prev => ({
          ...prev,
          horizontal: { width: horizontalRect.width }
        }));
      }
      if (verticalTrackRef.current) {
        const verticalRect = verticalTrackRef.current.getBoundingClientRect();
        setTrackDimensions(prev => ({
          ...prev,
          vertical: { height: verticalRect.height }
        }));
      }
    };

    updateTrackDimensions();
    window.addEventListener('resize', updateTrackDimensions);
    return () => window.removeEventListener('resize', updateTrackDimensions);
  }, []);

  // Calculate thumb positions and sizes
  const horizontalRatio = Math.min(1, viewBox.w / totalWidth);
  const verticalRatio = Math.min(1, viewBox.h / totalHeight);
  
  // Calculate thumb width/height percentages (minimum 10%)
  const horizontalThumbWidthPercent = Math.min(100, Math.max(10, horizontalRatio * 100));
  const verticalThumbHeightPercent = Math.min(100, Math.max(10, verticalRatio * 100));
  
  // FIXED: Calculate available movement range for thumb (100% - thumb size)
  const horizontalAvailableRange = Math.max(0, 100 - horizontalThumbWidthPercent);
  const verticalAvailableRange = Math.max(0, 100 - verticalThumbHeightPercent);
  
  // FIXED: Calculate thumb position within available range
  const horizontalThumbPos = totalWidth > viewBox.w 
    ? ((viewBox.x - minX) / (totalWidth - viewBox.w)) * horizontalAvailableRange
    : 0;
  const verticalThumbPos = totalHeight > viewBox.h 
    ? ((viewBox.y - minY) / (totalHeight - viewBox.h)) * verticalAvailableRange
    : 0;

  // Handle horizontal scroll thumb dragging
  const handleHorizontalMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (setActiveInteraction) setActiveInteraction('scrollbar');
    horizontalDragRef.current = {
      active: true,
      startX: e.clientX,
      startViewX: viewBox.x
    };
    
    const handleMouseMove = (moveEvent) => {
      if (!horizontalDragRef.current.active) return;
      
      const deltaX = moveEvent.clientX - horizontalDragRef.current.startX;
      // FIXED: Use available range for sensitivity calculation
      const deltaGraph = deltaX * ((totalWidth - viewBox.w) / (trackDimensions.horizontal.width - (trackDimensions.horizontal.width * horizontalThumbWidthPercent / 100))) || deltaX * 0.01;
      
      const newX = Math.max(
        minX, 
        Math.min(maxX - viewBox.w, horizontalDragRef.current.startViewX + deltaGraph)
      );
      
      if (onViewBoxChange) {
        onViewBoxChange(prev => ({ ...prev, x: newX }));
      }
    };
    
    const handleMouseUp = () => {
      horizontalDragRef.current.active = false;
      if (setActiveInteraction) setActiveInteraction(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [viewBox.x, viewBox.w, minX, maxX, totalWidth, onViewBoxChange, setActiveInteraction, trackDimensions.horizontal.width, horizontalThumbWidthPercent]);

  // Handle vertical scroll thumb dragging
  const handleVerticalMouseDown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (setActiveInteraction) setActiveInteraction('scrollbar');
    verticalDragRef.current = {
      active: true,
      startY: e.clientY,
      startViewY: viewBox.y
    };
    
    const handleMouseMove = (moveEvent) => {
      if (!verticalDragRef.current.active) return;
      
      const deltaY = moveEvent.clientY - verticalDragRef.current.startY;
      // FIXED: Use available range for sensitivity calculation
      const deltaGraph = deltaY * ((totalHeight - viewBox.h) / (trackDimensions.vertical.height - (trackDimensions.vertical.height * verticalThumbHeightPercent / 100))) || deltaY * 0.01;
      
      const newY = Math.max(
        minY, 
        Math.min(maxY - viewBox.h, verticalDragRef.current.startViewY + deltaGraph)
      );
      
      if (onViewBoxChange) {
        onViewBoxChange(prev => ({ ...prev, y: newY }));
      }
    };
    
    const handleMouseUp = () => {
      verticalDragRef.current.active = false;
      if (setActiveInteraction) setActiveInteraction(null);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [viewBox.y, viewBox.h, minY, maxY, totalHeight, onViewBoxChange, setActiveInteraction, trackDimensions.vertical.height, verticalThumbHeightPercent]);

  // Handle clicking on scrollbar track (jump to position)
  const handleHorizontalTrackClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - trackRect.left;
    const trackWidth = trackRect.width;
    
    // FIXED: Account for thumb width when calculating click position
    const thumbWidthPixels = trackWidth * horizontalThumbWidthPercent / 100;
    const availableTrackWidth = trackWidth - thumbWidthPixels;
    
    let clickRatio = 0;
    if (availableTrackWidth > 0) {
      clickRatio = Math.min(1, Math.max(0, clickX / availableTrackWidth));
    }
    
    const newX = minX + clickRatio * (totalWidth - viewBox.w);
    
    if (onViewBoxChange) {
      onViewBoxChange(prev => ({ 
        ...prev, 
        x: Math.max(minX, Math.min(maxX - viewBox.w, newX)) 
      }));
    }
  }, [minX, maxX, totalWidth, viewBox.w, onViewBoxChange, horizontalThumbWidthPercent]);

  const handleVerticalTrackClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const trackRect = e.currentTarget.getBoundingClientRect();
    const clickY = e.clientY - trackRect.top;
    const trackHeight = trackRect.height;
    
    // FIXED: Account for thumb height when calculating click position
    const thumbHeightPixels = trackHeight * verticalThumbHeightPercent / 100;
    const availableTrackHeight = trackHeight - thumbHeightPixels;
    
    let clickRatio = 0;
    if (availableTrackHeight > 0) {
      clickRatio = Math.min(1, Math.max(0, clickY / availableTrackHeight));
    }
    
    const newY = minY + clickRatio * (totalHeight - viewBox.h);
    
    if (onViewBoxChange) {
      onViewBoxChange(prev => ({ 
        ...prev, 
        y: Math.max(minY, Math.min(maxY - viewBox.h, newY)) 
      }));
    }
  }, [minY, maxY, totalHeight, viewBox.h, onViewBoxChange, verticalThumbHeightPercent]);

  // Only show scrollbars if content is larger than viewport
  const showHorizontalScroll = totalWidth > viewBox.w;
  const showVerticalScroll = totalHeight > viewBox.h;

  return (
    <>
      {/* Horizontal Scrollbar */}
      {showHorizontalScroll && (
        <div
          ref={horizontalTrackRef}
          style={{
            position: "absolute",
            left: 10,
            right: showVerticalScroll ? 20 : 10,
            bottom: 6,
            height: 4,
            background: "rgba(200, 200, 200, 0.3)",
            borderRadius: 4,
            zIndex: 1000,
            cursor: "pointer",
            overflow: "hidden"
          }}
          onClick={handleHorizontalTrackClick}
        >
          <div
            ref={horizontalThumbRef}
            style={{
              position: "absolute",
              left: `${horizontalThumbPos}%`,
              width: `${horizontalThumbWidthPercent}%`,
              height: "100%",
              background: horizontalDragRef.current.active 
                ? "rgba(80, 80, 80, 0.8)" 
                : "rgba(100, 100, 100, 0.6)",
              borderRadius: 4,
              cursor: "grab",
              transition: horizontalDragRef.current.active ? 'none' : 'background 0.2s, left 0.1s'
            }}
            onMouseDown={handleHorizontalMouseDown}
          />
        </div>
      )}

      {/* Vertical Scrollbar */}
      {showVerticalScroll && (
        <div
          ref={verticalTrackRef}
          style={{
            position: "absolute",
            right: 6,
            top: 10,
            bottom: showHorizontalScroll ? 20 : 10,
            width: 4,
            background: "rgba(200, 200, 200, 0.3)",
            borderRadius: 4,
            zIndex: 1000,
            cursor: "pointer",
            overflow: "hidden"
          }}
          onClick={handleVerticalTrackClick}
        >
          <div
            ref={verticalThumbRef}
            style={{
              position: "absolute",
              top: `${verticalThumbPos}%`,
              height: `${verticalThumbHeightPercent}%`,
              width: "100%",
              background: verticalDragRef.current.active 
                ? "rgba(80, 80, 80, 0.8)" 
                : "rgba(100, 100, 100, 0.6)",
              borderRadius: 4,
              cursor: "grab",
              transition: verticalDragRef.current.active ? 'none' : 'background 0.2s, top 0.1s'
            }}
            onMouseDown={handleVerticalMouseDown}
          />
        </div>
      )}
    </>
  );
}